import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ConstructionIcon from '@mui/icons-material/Construction';
import EditNoteIcon from '@mui/icons-material/EditNote';
import InboxIcon from '@mui/icons-material/Inbox';
import CalculateIcon from '@mui/icons-material/Calculate';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SavingsIcon from '@mui/icons-material/Savings';
import PaymentsIcon from '@mui/icons-material/Payments';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';

export const PERMISSIONS = {
  FILED: {
    LIST: 'filed.list',
    CREATE: 'filed.create',
    READ: 'filed.read',
    UPDATE: 'filed.update',
    DELETE: 'filed.delete',
    CANCEL: 'filed.cancel',
    // Configures the radicados module (OCR switch). Reserved for ADMIN, the
    // same way PQRSD.CONFIG is kept out of PQRSD_SUPERVISOR.
    CONFIG: 'filed.config'
  },
  REQUESTSFILED: {
    LIST: 'requestsFiled.list',
    CREATE: 'requestsFiled.create',
    READ: 'requestsFiled.read',
    UPDATE: 'requestsFiled.update',
    DELETE: 'requestsFiled.delete',
    RESEND_EMAIL: 'requestsFiled.resend_email',
  },
  TASK: {
    LIST: 'task.list'
  },
  PROCESS: {
    LIST: 'process.list',
    CREATE: 'process.create',
    READ: 'process.read',
    UPDATE: 'process.update',
    DELETE: 'process.delete',
    INSTANCE: 'process.instance',
    READ_FLOW: 'process.read_flow',
    CANCEL_FLOW: 'process.cancel_flow',
    SAVE_AS_ANY_EXPEDIENT: 'process.save_as_any_expedient',
    VIEW_INPROGRESS: 'process.view_inprogress',
    EDITOR_OPEN_FILE: 'process.editor_open_file'
  },
  EXPEDIENT: {
    VIEW: 'expedient.view',
    OWN: 'expedient.own',
    CREATE_TYPE: 'expedient.create_type',
    UPDATE_TYPE: 'expedient.update_type',
    DELETE_TYPE: 'expedient.delete_type',
    LIST: 'expedient.list',
    CREATE: 'expedient.create',
    READ: 'expedient.read',
    UPDATE: 'expedient.update',
    DELETE: 'expedient.delete',
    CREATE_DOCUMENT: 'expedient.create_document'
  },
  // Tabla de Retención Documental (módulo /trd). The backend (handleTrd, in
  // gfunctions/trd) authorizes each action by one of these, read from the
  // caller's STORED permissions: an archiver saved before this group existed
  // must be saved again. ADMIN passes by role, like the other modules.
  TRD: {
    VIEW: 'trd.view',
    CREATE: 'trd.create',
    UPDATE: 'trd.update',
    PUBLISH: 'trd.publish',
    EXPORT: 'trd.export'
  },
  CONTRACT: {
    LIST: 'contract.list',
    CREATE: 'contract.create',
    READ: 'contract.read',
    UPDATE: 'contract.update',
    DELETE: 'contract.delete',
    APPROVE: 'contract.approve',
    CLOSE: 'contract.close',
    CONSOLIDATE: 'contract.consolidate'
  },
  // Contratos de personas jurídicas (colección contractsLegal). Dominio propio y
  // no un reuso de CONTRACT.*: quien gestiona contratos naturales no gestiona
  // jurídicos por el solo hecho de serlo, y al revés. ADMIN NO lo hereda (ver el
  // filtro en ROLES.ADMIN): el módulo nace apagado y se enciende usuario a
  // usuario, o con el rol ADMIN_CONTRACT_LEGAL.
  CONTRACT_LEGAL: {
    LIST: 'contract_legal.list',
    CREATE: 'contract_legal.create',
    READ: 'contract_legal.read',
    UPDATE: 'contract_legal.update',
    DELETE: 'contract_legal.delete',
    APPROVE: 'contract_legal.approve',
    CLOSE: 'contract_legal.close'
  },
  SUPERVISOR_CONTRACT: {
    LIST: 'supervisor_contract.list',
    UPDATE: 'supervisor_contract.update',
    APPROVE: 'supervisor_contract.approve',
    // Aprobar una cuenta de cobro VACÍA (periodo sin actividades), saltando
    // todos los prerrequisitos de revisión. Existe para los contratos montados
    // tarde, cuyos primeros periodos deben cerrarse para habilitar el mes
    // corriente. Se otorga por separado (por defecto solo lo lleva ADMIN): no
    // va implícito en APPROVE ni en la supervisión del contrato.
    APPROVE_EMPTY: 'supervisor_contract.approve_empty',
    // Revertir la aprobación de la ÚLTIMA cuenta de cobro aprobada: borra la
    // aprobación y sus metadatos (fecha, PDF, hash) y devuelve el acta a
    // revisión del Supervisor. Excepción administrativa como APPROVE_EMPTY:
    // por defecto solo la lleva ADMIN.
    REVERT_APPROVAL: 'supervisor_contract.revert_approval',
    REJECT_ACTIVITY: 'supervisor_contract.reject_activity'
  },
  SUPPORT_SUPERVISION: {
    LIST: 'support_supervision.list',
    REVIEW: 'support_supervision.review',
    RETURN: 'support_supervision.return'
  },
  CONTRACTOR: {
    LIST: 'contractor.list',
    UPDATE: 'contractor.update'
  },
  REPORT: {
    LIST: 'report.list'
  },
  TEMPLATE: {
    LIST: 'template.list',
    CREATE: 'template.create',
    READ: 'template.read',
    UPDATE: 'template.update',
    DELETE: 'template.delete'
  },
  LAB: {
    LIST: 'lab.list'
  },
  CONFIG: {
    LIST: 'config.list'
  },
  AUDIT: {
    LIST: 'audit.list'
  },
  FINANCING: {
    LIST: 'financing.list',
  },
  // Recorrido financiero de las solicitudes de pago juridicas (acta #3 del
  // 2026-09-10): un permiso por etapa, resuelto por area y no por contrato. LIST
  // abre la bandeja de Gestion de Pagos y la trazabilidad; cada permiso de etapa
  // habilita las acciones SOLO sobre las solicitudes que estan en esa etapa (la
  // tabla PAYMENT_STAGES de paymentRequestLogic.js es quien lo impone). No toca
  // FINANCING, que sigue siendo el flujo de personas naturales.
  PAYMENT_FLOW: {
    LIST: 'payment_flow.list',
    CAUSATION: 'payment_flow.causation',
    ACCOUNTING: 'payment_flow.accounting',
    BUDGET: 'payment_flow.budget',
    TREASURY: 'payment_flow.treasury',
    CASHIER: 'payment_flow.cashier',
  },
  USER_ADMIN: {
    ADD: 'user_admin.add',
    MANAGE: 'user_admin.manage',
    DEACTIVATE: 'user_admin.deactivate',
  },
  PQRSD: {
    // Supervisor-level perms (see/manage all PQRSDs).
    LIST: 'pqrsd.list',
    ASSIGN: 'pqrsd.assign',
    CLOSE: 'pqrsd.close',
    EXPORT: 'pqrsd.export',
    APPROVE_RESPONSE: 'pqrsd.approve_response',
    // Decrees the desistimiento tácito of a PQRSD whose requerimiento de
    // información expired (CPACA art. 17). Kept above the operator line on
    // purpose: it archives the petition by acto administrativo motivado, so it
    // is never a plain operator action. Held by the general supervisor
    // (Object.values spread below) and by the dependency supervisor, who is the
    // one who audits the expired requerimientos of their own dependency — the
    // backend additionally scopes them to that dependency.
    DECREE_DESISTIMIENTO: 'pqrsd.decree_desistimiento',
    // Operator-level perms.
    READ: 'pqrsd.read',
    RESPOND: 'pqrsd.respond',
    START_RESPONSE_FLOW: 'pqrsd.start_response_flow',
    MINE: 'pqrsd.mine',
    RETURN: 'pqrsd.return',
    FORWARD: 'pqrsd.forward',
    // Requires the citizen to complete an incomplete petition (CPACA art. 17):
    // build and send a requerimiento de información, which suspends the SLA
    // until the citizen answers. Held by whoever is actually working the
    // PQRSD — the assignee or a member of the routed dependency.
    REQUEST_INFO: 'pqrsd.request_info',
    // Dependency-supervisor perms. ASSIGN_GROUP lets a designated dependency
    // lead assign PQRSDs routed to their dependency to that dependency's own
    // members (a scoped subset of ASSIGN). RETURN_TRIAGE bounces a misrouted
    // PQRSD back to Triage (with a reason) so the general supervisor re-routes
    // it to another dependency. Both auto-granted to PQRSD_SUPERVISOR via the
    // Object.values spread below.
    ASSIGN_GROUP: 'pqrsd.assign_group',
    RETURN_TRIAGE: 'pqrsd.return_triage',
    // Window/front-desk intake: radicar PQRSD por ventanilla on behalf of a
    // citizen. Held by the dedicated PQRSD_WINDOW role (and, by virtue of the
    // Object.values spread, by PQRSD_SUPERVISOR).
    WINDOW_INTAKE: 'pqrsd.window_intake',
    // Manage the PQRSD AI configuration module: CRUD user groups (dependencias)
    // and tune the AI classifier (system prompt, model, confidence threshold).
    // Auto-granted to PQRSD_SUPERVISOR via the Object.values spread below.
    CONFIG: 'pqrsd.config',
    // Read-only analytics/informes dashboard (attention + AI-classification
    // stats). Held by the dedicated PQRSD_ANALYST role and, via the
    // Object.values spread below, auto-granted to PQRSD_SUPERVISOR.
    REPORTS: 'pqrsd.reports',
  },
  // Módulo Extensión — Aspirantes (banco de postulantes / hojas de vida).
  // Fase 1 (registro público) no requiere permiso: es anónimo y protegido por
  // App Check. Fase 2 (gestión) la ejerce el gestor de contratos (ADMIN_CONTRACT).
  APPLICANT: {
    LIST: 'applicant.list',
    READ: 'applicant.read',
    SEARCH: 'applicant.search',
    VALIDATE_DOC: 'applicant.validate_doc',
    SELECT: 'applicant.select',
  },
  // Módulo Extensión — Convenios (registro de convenios y contratos
  // interadministrativos de la Dirección de Extensión, con su presupuesto).
  AGREEMENT: {
    LIST: 'agreement.list',
    READ: 'agreement.read',
    CREATE: 'agreement.create',
    UPDATE: 'agreement.update',
    DELETE: 'agreement.delete',
  },
  // Módulo Extensión — Entidades (las organizaciones con las que se firman los
  // convenios: su identificación, sus datos de contacto y su documentación).
  // VALIDATE_DOC va aparte de UPDATE porque cargar un documento y dar fe de que
  // sirve son dos trabajos distintos, igual que en APPLICANT.
  ENTITY: {
    LIST: 'entity.list',
    READ: 'entity.read',
    CREATE: 'entity.create',
    UPDATE: 'entity.update',
    DELETE: 'entity.delete',
    VALIDATE_DOC: 'entity.validate_doc',
    // Aprobar o rechazar una entidad que se registró sola por el portal. Va
    // aparte de UPDATE por lo mismo que VALIDATE_DOC: corregirle un dato a una
    // entidad y dar fe de que la entidad existe son dos trabajos distintos.
    APPROVE: 'entity.approve',
  },
  // What a user of the entity ITSELF may do to it. Separate from ENTITY, which
  // is the internal manager's permission over ANY entity.
  //
  // This is the app's first SCOPED permission: holding it does not say which
  // entity. Scope is imposed by the backend, which forces the actor's NIT before
  // dispatching the action (see handleEntity.js), not by the permission.
  MY_ENTITY: {
    VIEW: 'my_entity.view',
    UPDATE: 'my_entity.update',
    UPLOAD_DOC: 'my_entity.upload_doc',
    MANAGE_CONTACTS: 'my_entity.manage_contacts',
    INVITE_MEMBER: 'my_entity.invite_member',
    MANAGE_MEMBERS: 'my_entity.manage_members',
  },
};

// Account type. Internal staff carry NO mark: its absence means staff, so
// records created before this field existed remain staff with no backfill.
export const USER_TYPE = {
  ENTITY: 'ENTITY',
};

export const ROLES = {
  // ADMIN takes every permission EXCEPT MY_ENTITY's and CONTRACT_LEGAL's.
  //
  // MY_ENTITY describes what a company does to ITS OWN record, and an internal
  // administrator has no entity of their own — inheriting them would only paint
  // a "Mi Entidad" tile on the home screen that leads nowhere.
  //
  // CONTRACT_LEGAL is left out so the persona jurídica module ships DARK: it is
  // released before the institution has agreed who operates it, and inheriting
  // it here would light it up for every administrator on deploy day. Grant it
  // with ADMIN_CONTRACT_LEGAL, or permission by permission from /admin.
  ADMIN: Object.keys(PERMISSIONS)
    .filter((k) => k !== 'MY_ENTITY' && k !== 'CONTRACT_LEGAL')
    .map((k) => Object.values(PERMISSIONS[k])).flat(),
  ARCHIVER: [
    // Excludes CONFIG — the radicados module configuration (OCR) is ADMIN-only.
    // Includes CANCEL: the institution requires the archive to annul radicados.
    ...Object.values(PERMISSIONS.FILED).filter((p) => p !== PERMISSIONS.FILED.CONFIG),
    ...Object.values(PERMISSIONS.REQUESTSFILED),
    ...Object.values(PERMISSIONS.TASK),
    ...Object.values(PERMISSIONS.PROCESS).filter((p) => ![
      PERMISSIONS.PROCESS.CANCEL_FLOW,
      PERMISSIONS.PROCESS.VIEW_INPROGRESS,
      PERMISSIONS.PROCESS.EDITOR_OPEN_FILE
    ].includes(p)),
    ...Object.values(PERMISSIONS.EXPEDIENT),
    // The archive owns the TRD that classifies those expedientes.
    ...Object.values(PERMISSIONS.TRD),
    ...Object.values(PERMISSIONS.REPORT),
    ...Object.values(PERMISSIONS.AUDIT),
  ],
  // Consulta de expedientes: the module's list and every expediente in it.
  // Until this role existed it was granted by hand, as these two permissions
  // (the "Perfil de Expedientes" template); the Informes de Usuarios list
  // counts an account holding both as having the role.
  EXPEDIENTS: [
    PERMISSIONS.EXPEDIENT.VIEW,
    PERMISSIONS.EXPEDIENT.LIST,
  ],
  PQRSD_SUPERVISOR: [
    // Full management of every PQRSD: see all, assign, respond, close, export.
    // Excludes CONFIG — the PQRSD module configuration (dependencies + AI
    // classifier) is reserved for the dedicated PQRSD_CONFIG role (and ADMIN).
    ...Object.values(PERMISSIONS.PQRSD).filter((p) => p !== PERMISSIONS.PQRSD.CONFIG),
    ...Object.values(PERMISSIONS.REQUESTSFILED).filter((p) => p !== PERMISSIONS.REQUESTSFILED.DELETE),
    PERMISSIONS.FILED.LIST,
    PERMISSIONS.FILED.READ,
    PERMISSIONS.FILED.CREATE,
    PERMISSIONS.FILED.UPDATE,
  ],
  PQRSD_DEPENDENCY_SUPERVISOR: [
    // Dependency-level lead: a PQRSD operator who can also assign PQRSDs routed
    // to their dependency to that dependency's members, and return a misrouted
    // PQRSD to Triage. Same base powers as PQRSD_OPERATOR plus ASSIGN_GROUP and
    // RETURN_TRIAGE. No LIST/ASSIGN/CLOSE/CONFIG (those stay supervisor/admin).
    PERMISSIONS.PQRSD.READ,
    PERMISSIONS.PQRSD.RESPOND,
    PERMISSIONS.PQRSD.START_RESPONSE_FLOW,
    PERMISSIONS.PQRSD.MINE,
    PERMISSIONS.PQRSD.RETURN,
    PERMISSIONS.PQRSD.FORWARD,
    PERMISSIONS.PQRSD.REQUEST_INFO,
    PERMISSIONS.PQRSD.ASSIGN_GROUP,
    PERMISSIONS.PQRSD.RETURN_TRIAGE,
    // Audits the requerimientos of their dependency that expired without an
    // answer, and decrees the desistimiento on them. Scoped server-side to the
    // dependencies they supervise (pqrsdGroups.supervisorIds).
    PERMISSIONS.PQRSD.DECREE_DESISTIMIENTO,
    ...Object.values(PERMISSIONS.REQUESTSFILED).filter((p) =>
      [PERMISSIONS.REQUESTSFILED.LIST, PERMISSIONS.REQUESTSFILED.READ, PERMISSIONS.REQUESTSFILED.CREATE].includes(p)
    ),
    PERMISSIONS.FILED.READ,
    PERMISSIONS.FILED.CREATE,
    PERMISSIONS.FILED.UPDATE,
  ],
  PQRSD_OPERATOR: [
    // Scoped: only see/respond to PQRSDs assigned to them. No LIST/ASSIGN/CLOSE.
    PERMISSIONS.PQRSD.READ,
    PERMISSIONS.PQRSD.RESPOND,
    PERMISSIONS.PQRSD.START_RESPONSE_FLOW,
    PERMISSIONS.PQRSD.MINE,
    PERMISSIONS.PQRSD.RETURN,
    PERMISSIONS.PQRSD.FORWARD,
    PERMISSIONS.PQRSD.REQUEST_INFO,
    ...Object.values(PERMISSIONS.REQUESTSFILED).filter((p) =>
      [PERMISSIONS.REQUESTSFILED.LIST, PERMISSIONS.REQUESTSFILED.READ, PERMISSIONS.REQUESTSFILED.CREATE].includes(p)
    ),
    PERMISSIONS.FILED.READ,
    PERMISSIONS.FILED.CREATE,
    PERMISSIONS.FILED.UPDATE,
  ],
  PQRSD_WINDOW: [
    // Front-desk staff who only radicate PQRSDs por ventanilla on behalf of
    // citizens. No queue/management access; filings land unassigned in the
    // supervisor queue. Annexes upload through the intake multipart, so no
    // filed.* perms are required.
    PERMISSIONS.PQRSD.WINDOW_INTAKE,
  ],
  PQRSD_CONFIG: [
    // Configures the PQRSD AI module: CRUD dependency groups and tune the
    // classifier. Scoped to configuration only — no queue/management access.
    PERMISSIONS.PQRSD.CONFIG,
  ],
  PQRSD_ANALYST: [
    // Read-only PQRSD analytics/informes. Sees the Informes dashboard only —
    // no queue, intake, or config access. PQRSD_SUPERVISOR/ADMIN inherit this
    // permission automatically (see the Object.values spread above).
    PERMISSIONS.PQRSD.REPORTS,
  ],
  USER: [
    ...Object.values(PERMISSIONS.REQUESTSFILED).filter((p) => p !== PERMISSIONS.REQUESTSFILED.RESEND_EMAIL),
    ...Object.values(PERMISSIONS.TASK),
    ...Object.values(PERMISSIONS.PROCESS).filter((p) => ![
      PERMISSIONS.PROCESS.CANCEL_FLOW,
      PERMISSIONS.PROCESS.VIEW_INPROGRESS,
      PERMISSIONS.PROCESS.SAVE_AS_ANY_EXPEDIENT,
      PERMISSIONS.PROCESS.EDITOR_OPEN_FILE
    ].includes(p)),
    PERMISSIONS.EXPEDIENT.OWN
  ],
  ADMIN_CONTRACT: [
    ...Object.values(PERMISSIONS.CONTRACT),
    ...Object.values(PERMISSIONS.FINANCING),
    // El módulo Extensión NO entra aquí: gestionar el banco de aspirantes es
    // trabajo de EXTENSION_APPLICANTS. Quien deba hacer ambas cosas lleva los dos
    // roles, en vez de que todo gestor de contratos vea las hojas de vida.
    // Los contratos de personas jurídicas tampoco: son ADMIN_CONTRACT_LEGAL, por
    // el mismo criterio. Gestionar contratos naturales no habilita los jurídicos.
  ],
  // Gestor de contratos de personas jurídicas. Espejo de ADMIN_CONTRACT sobre el
  // dominio propio. Quien gestione ambas clases lleva los dos roles.
  ADMIN_CONTRACT_LEGAL: [
    ...Object.values(PERMISSIONS.CONTRACT_LEGAL),
  ],
  EXTENSION_APPLICANTS: [
    // Módulo Extensión — gestor del banco de aspirantes. Junto con ADMIN, el
    // único rol que ve el módulo.
    ...Object.values(PERMISSIONS.APPLICANT),
  ],
  EXTENSION_AGREEMENTS: [
    // Módulo Extensión — administración de convenios. Rol propio y no parte de
    // EXTENSION_APPLICANTS: llevar el registro de convenios y su presupuesto es
    // otro oficio que revisar hojas de vida. Quien haga ambas cosas lleva los
    // dos roles.
    ...Object.values(PERMISSIONS.AGREEMENT),
    // Todo convenio apunta a una entidad registrada, así que sin poder
    // consultarlas este rol no podría ni crear un convenio. Sólo lectura:
    // mantener el registro de entidades es EXTENSION_ENTITIES.
    PERMISSIONS.ENTITY.LIST,
    PERMISSIONS.ENTITY.READ,
  ],
  EXTENSION_ENTITIES: [
    // Módulo Extensión — registro de entidades: sus datos y su documentación.
    // Separado de EXTENSION_AGREEMENTS porque recibir y validar los papeles de
    // una entidad es trabajo de ventanilla, no de administración presupuestal.
    ...Object.values(PERMISSIONS.ENTITY),
    // Ve los convenios de la entidad desde su ficha, pero no los administra.
    PERMISSIONS.AGREEMENT.LIST,
    PERMISSIONS.AGREEMENT.READ,
  ],
  SUPERVISOR_CONTRACT: [
    // Excluye APPROVE_EMPTY y REVERT_APPROVAL: aprobar cuentas de cobro vacías
    // y revertir una aprobación son excepciones administrativas que por
    // defecto solo ejerce ADMIN. Se conceden usuario a usuario desde la
    // administración de permisos.
    ...Object.values(PERMISSIONS.SUPERVISOR_CONTRACT).filter((p) => ![
      PERMISSIONS.SUPERVISOR_CONTRACT.APPROVE_EMPTY,
      PERMISSIONS.SUPERVISOR_CONTRACT.REVERT_APPROVAL,
    ].includes(p)),
    PERMISSIONS.CONTRACT.READ
  ],
  // Supervisor de contratos de personas jurídicas (contractsLegal). Porta las
  // mismas cadenas de permiso del supervisor natural: el alcance real lo da la
  // asignación en supervisorIDs del contrato, no el permiso. Una persona puede
  // tener ambos roles.
  SUPERVISOR_LEGAL_CONTRACT: [
    // Excluye APPROVE_EMPTY y REVERT_APPROVAL por el mismo motivo que
    // SUPERVISOR_CONTRACT: son excepciones administrativas que se conceden
    // usuario a usuario. Sin este filtro el espejo dejaba de serlo y el
    // supervisor jurídico aprobaba cuentas de cobro vacías que el natural no.
    ...Object.values(PERMISSIONS.SUPERVISOR_CONTRACT).filter((p) => ![
      PERMISSIONS.SUPERVISOR_CONTRACT.APPROVE_EMPTY,
      PERMISSIONS.SUPERVISOR_CONTRACT.REVERT_APPROVAL,
    ].includes(p)),
    // Lee el contrato por el dominio jurídico, no por CONTRACT.READ: supervisar
    // una persona jurídica no da acceso a los contratos naturales.
    PERMISSIONS.CONTRACT_LEGAL.READ
  ],
  SUPPORT_SUPERVISION: [
    ...Object.values(PERMISSIONS.SUPPORT_SUPERVISION),
    PERMISSIONS.CONTRACT.READ,
    // El apoyo edita el contrato con los mismos alcances del supervisor
    // mientras esté en supportIDs, en cualquier estado (los guards de
    // ContractForm/ContractItem imponen la asignación).
    PERMISSIONS.SUPERVISOR_CONTRACT.UPDATE
  ],
  CONTRACTOR: [
    ...Object.values(PERMISSIONS.CONTRACTOR),
    PERMISSIONS.CONTRACT.READ
  ],
  // Contratista persona juridica: el usuario-empresa (users.userType 'ENTITY',
  // vinculado por entityNit). Espejo exacto de CONTRACTOR, igual que
  // SUPERVISOR_LEGAL_CONTRACT lo es de SUPERVISOR_CONTRACT: el alcance real no
  // lo da el permiso sino el entityNit, que el backend resuelve desde
  // users/{uid} y nunca desde el body de la peticion.
  CONTRACTOR_LEGAL: [
    ...Object.values(PERMISSIONS.CONTRACTOR),
    // Igual que SUPERVISOR_LEGAL_CONTRACT: lee por el dominio jurídico. La
    // empresa no tiene nada que hacer en los contratos de personas naturales.
    PERMISSIONS.CONTRACT_LEGAL.READ
  ],
  FINANCIER: [
    PERMISSIONS.FINANCING.LIST
  ],
  // Areas del recorrido financiero de facturas juridicas. Un rol por area para
  // poder asignarlos desde /admin a quien corresponda (Causacion, Contabilidad,
  // Presupuesto, Tesoreria, Caja); son aditivos, una persona puede portar
  // varios. FINANCIER NO cambia: filtra por naturalezas el flujo natural y
  // ampliarlo desfasaria el snapshot de permisos del backend.
  PAYMENT_CAUSATION: [PERMISSIONS.PAYMENT_FLOW.LIST, PERMISSIONS.PAYMENT_FLOW.CAUSATION],
  PAYMENT_ACCOUNTING: [PERMISSIONS.PAYMENT_FLOW.LIST, PERMISSIONS.PAYMENT_FLOW.ACCOUNTING],
  PAYMENT_BUDGET: [PERMISSIONS.PAYMENT_FLOW.LIST, PERMISSIONS.PAYMENT_FLOW.BUDGET],
  PAYMENT_TREASURY: [PERMISSIONS.PAYMENT_FLOW.LIST, PERMISSIONS.PAYMENT_FLOW.TREASURY],
  PAYMENT_CASHIER: [PERMISSIONS.PAYMENT_FLOW.LIST, PERMISSIONS.PAYMENT_FLOW.CASHIER],
  ADMIN_USER: [
    ...Object.values(PERMISSIONS.USER_ADMIN)
  ],
  // ---------------------------------------------------------------------
  // Company users. Not assigned by hand from /admin: they come from public
  // registration (the founder) or from an invitation (the rest of the company's
  // staff). See gfunctions/entities/entityAuth.js.
  // ---------------------------------------------------------------------
  ENTITY_ADMIN: [
    // Whoever registered the company, and whoever they promote: keeps the record
    // current and invites the rest of their staff.
    ...Object.values(PERMISSIONS.MY_ENTITY),
    // Their own legal contracts. The permission is the same one a natural-person
    // contractor holds, and it is not a wider door: handleContractLegal resolves
    // the entityNit from the ACTOR's own user document and filters by it, so the
    // company reads its own contracts and nobody else's.
    PERMISSIONS.CONTRACTOR.LIST
  ],
  ENTITY_MEMBER: [
    // Invited staff: reads the record and uploads documents. Does not touch the
    // company's identity data or administer colleagues.
    PERMISSIONS.MY_ENTITY.VIEW,
    PERMISSIONS.MY_ENTITY.UPLOAD_DOC
  ]
};

// Roles only a company user can hold, and which for that reason are not offered
// in the staff sign-up at /admin.
export const ENTITY_ROLES = ['ENTITY_ADMIN', 'ENTITY_MEMBER'];

// PQRSD types. Keys are stored in filed.pqrsd.type. Labels are UI-facing.
export const PQRSD_TYPES = {
  peticion_general: 'Petición general',
  consulta: 'Petición de consulta',
  info: 'Solicitud de información o documentación',
  queja: 'Queja',
  reclamo: 'Reclamo',
  denuncia: 'Denuncia',
  sugerencia: 'Sugerencia',
  felicitacion: 'Felicitación',
};

// SLA in Colombian business days per PQRSD type (Law 1755/2015 reference).
// Felicitacion/sugerencia have no strict deadline; we set 15 for tracking.
export const PQRSD_SLA_DAYS = {
  peticion_general: 15,
  consulta: 30,
  info: 10,
  queja: 15,
  reclamo: 15,
  denuncia: 15,
  sugerencia: 15,
  felicitacion: 15,
};

export const PQRSD_STATUS = {
  RADICADO: 'radicado',
  ASIGNADO: 'asignado',
  EN_TRAMITE: 'en_tramite',
  RESPONDIDO: 'respondido',
  CERRADO: 'cerrado',
  DEVUELTO: 'devuelto',
  REABIERTO: 'reabierto',
  // Suspended by a requerimiento de información (CPACA art. 17): the entity's
  // term is paused (pqrsd.slaPaused) while the citizen completes the petition.
  PENDIENTE_INFO: 'pendiente_info',
  // Terminal: the citizen never completed the requerimiento and a supervisor
  // decreed the desistimiento tácito by acto administrativo motivado.
  DESISTIDO: 'desistido',
  // The citizen filed a recurso de reposición against the desistimiento; the
  // entity decides it (reponer → back to trámite, confirmar → desistido).
  EN_RECURSO: 'en_recurso',
  // Set when its radicado is annulled (handleCorrespondence CANCEL_FILED), which
  // is only allowed before any response is filed or in progress. Terminal.
  ANULADO: 'anulado',
};

// ---------------------------------------------------------------------------
// Módulo Extensión — Aspirantes / Postulaciones
// ---------------------------------------------------------------------------

// Estado de una postulación por convocatoria (aspirantes/{nuip}/postulaciones/{postulacionId};
// "general" es el registro permanente). Impulsa el reanudar (currentStep) y el
// barrido de postulaciones incompletas.
export const APPLICATION_STATUS = {
  DRAFT: 'DRAFT',
  APPLIED: 'APPLIED',
  IN_REVIEW: 'IN_REVIEW',
  SELECTED: 'SELECTED',
  DOCS_STAGE2_PENDING: 'DOCS_STAGE2_PENDING',
  COMPLETE: 'COMPLETE',
  REJECTED: 'REJECTED',
};

// ---------------------------------------------------------------------------
// Módulo Extensión — Convenios
// ---------------------------------------------------------------------------

// Naturaleza del registro: un convenio (interadministrativo, con una entidad
// externa) o un contrato. Se guarda en inglés; las etiquetas en español están en
// components/agreements/agreementLabels.js.
export const AGREEMENT_TYPE = {
  CONTRACT: 'CONTRACT',
  AGREEMENT: 'AGREEMENT',
};

// Etapa del convenio dentro de su ciclo de vida.
export const AGREEMENT_STATUS = {
  UNDER_REVIEW: 'UNDER_REVIEW',   // en estudio
  IN_PROGRESS: 'IN_PROGRESS',     // en ejecución
  FINISHED: 'FINISHED',           // finalizado
  SETTLED: 'SETTLED',             // liquidado
};

// ---------------------------------------------------------------------------
// Módulo Extensión — Entidades
// ---------------------------------------------------------------------------

// Naturaleza jurídica de la entidad. Determina qué documentos se le exigen: una
// alcaldía no tiene cámara de comercio y una empresa privada no tiene acto
// administrativo de creación, así que el catálogo de la configuración marca cada
// documento con los tipos que lo requieren.
export const ENTITY_TYPE = {
  PUBLIC: 'PUBLIC',               // pública
  PRIVATE: 'PRIVATE',             // privada
  MIXED: 'MIXED',                 // mixta
  NONPROFIT: 'NONPROFIT',         // sin ánimo de lucro (ESAL)
  INTERNATIONAL: 'INTERNATIONAL', // organismo internacional
};

// PENDING es el estado en que cae una entidad que se registró SOLA por el portal
// público: existe y tiene sus papeles, pero nadie de Extensión la ha mirado. No
// se puede firmar un convenio con ella hasta que un gestor la apruebe
// (ver loadEntityForWrite en gfunctions/extension/agreements/handleAgreement.js). Una
// entidad creada a mano por un gestor nace ACTIVE: crearla YA es aprobarla.
export const ENTITY_STATUS = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
};

// Por dónde entró la entidad. Sirve para distinguir en la lista lo que llegó del
// portal de lo que tecleó un gestor, que son dos grados de confianza distintos.
export const ENTITY_REGISTRATION_SOURCE = {
  PUBLIC: 'PUBLIC',
  STAFF: 'STAFF',
};

// Naturaleza de un teléfono de contacto. Importa porque decide si el número es
// VERIFICABLE: a un fijo no le llega un SMS, así que se guarda sin verificar y
// nunca entra al índice con el que se resuelve el NIT en el login por OTP.
export const PHONE_TYPE = {
  MOBILE: 'MOBILE',     // celular
  LANDLINE: 'LANDLINE', // teléfono fijo
};

// Estado de UN archivo dentro de un documento de la entidad. Mismo vocabulario
// que los documentos del aspirante, para que la tabla de revisión sea la misma.
//
// VENCIDO no está aquí a propósito: depende de la fecha de HOY y no de la última
// escritura, así que se deriva al leer (ver isExpired en entityLogic). Guardarlo
// sería un dato que se vuelve mentira solo, sin que nadie escriba nada.
export const ENTITY_DOC_FILE_STATUS = {
  PENDING: 'PENDING',
  UPLOADED: 'UPLOADED',
  VALIDATED: 'VALIDATED',
  REJECTED: 'REJECTED',
};

// Estado de la documentación consolidada de la entidad.
export const ENTITY_DOC_STATUS = {
  PENDING: 'PENDING',         // no se ha cargado nada
  INCOMPLETE: 'INCOMPLETE',   // falta algún documento obligatorio
  COMPLETE: 'COMPLETE',       // están todos, sin revisar del todo
  VALIDATED: 'VALIDATED',     // están todos y todos validados
};

// Estado de la documentación consolidada del aspirante.
export const APPLICANT_DOC_STATUS = {
  PENDING: 'PENDING',
  INCOMPLETE: 'INCOMPLETE',
  COMPLETE: 'COMPLETE',
  VALIDATED: 'VALIDATED',
};

// Checklists de documentos POR POSTULACIÓN — SEMILLA por defecto únicamente.
// La fuente viva es el doc Firestore config/applicants (editable por ADMIN en
// Extensión → Configuración de aspirantes), leído en el front por
// ApplicantConfigService y en el backend por gfunctions/extension/_shared/
// applicantConfig. Estas listas sólo siembran la configuración y el fallback del
// backend cuando el doc de config aún no existe.
//
// Etapa 1 (postulación): vacía por defecto. Los soportes de formación y
// experiencia NO son documentos de convocatoria: son documentos de perfil atados
// a cada registro (config/applicants.profileDocumentCatalog). Una convocatoria
// puede añadir aquí los suyos propios.
export const APPLICANT_REQUIRED_DOCS_STAGE1 = [];
// Etapa 2 (tras selección): antecedentes y documentos administrativos.
export const APPLICANT_REQUIRED_DOCS_STAGE2 = [
  'antecedente_judicial',
  'antecedente_disciplinario',
  'antecedente_fiscal',
  'antecedente_correctivos',
  'antecedente_redam',
  'antecedente_delitos_sexuales',
  'rut',
  'certificado_eps',
];

// Parámetros del OTP de acceso de aspirantes (login por correo).
// DEBEN mantenerse en sync con gfunctions/extension/aspiranteOtp.
// El canal de correo usa este OTP servidor (códigos en `otpAspirante`).
// El canal de teléfono NO usa este OTP: la verificación se hace con Firebase
// Phone Auth en el cliente y el backend valida el ID token resultante.
export const OTP_PARAMS = {
  codeLength: 6,
  expiryMinutes: 10,
  maxAttempts: 3,
};

// Prefijo país por defecto (Colombia) para normalizar celulares a E.164 antes
// de enviarlos a Firebase Phone Auth. Fuente única para el helper toE164.
export const DEFAULT_PHONE_PREFIX = "+57";

// Anexos de aspirantes — SEMILLA/tope de seguridad. El límite vivo por archivo
// es config/aspirantes.maxFileBytes (ver AspiranteConfigService); esta constante
// sólo siembra el default y sirve de tope duro en el backend.
export const APPLICANT_MAX_FILES = 15;
export const APPLICANT_MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

// Anexos de entidades — SEMILLA. El límite vivo por archivo es
// config/entities.entityMaxFileBytes (ver EntityConfigService); esta
// constante sólo siembra el default del formulario público.
export const ENTITY_MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
// Tope del logo. Aparte del de los documentos: es una imagen que se pinta en la
// ficha, no un papel que haya que archivar.
export const ENTITY_MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2 MB

// Per-cycle cap of operator-driven interim responses (each one resets the SLA).
// After this many interims, the operator must file a FINAL response.
export const PQRSD_MAX_INTERIMS_PER_CYCLE = 2;

// Default number of citizen reopenings allowed over a PQRSD's lifetime. The
// real cap is per institution: Config → PQRSD → General (config/pqrsd.
// maxReopens, 0 = no limit), applied by handlePqrsdPublic through
// _shared/reopenPolicy.js. Kept for the lab bench's default-value check.
export const PQRSD_MAX_REOPENS = 5;

// Annex upload limits — MUST stay in sync with the server enforcement in
// gfunctions/pqrsd/intakePqrsd/index.js (MAX_FILES, MAX_FILE_BYTES,
// ALLOWED_MIME_PREFIXES). The form and the lab test bench both read these so
// the UI never lets a citizen upload something the cloud function will reject.
export const PQRSD_MAX_FILES = 10;
export const PQRSD_MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
export const PQRSD_ALLOWED_MIME_PREFIXES = [
  'image/',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument', // .docx / .xlsx / .pptx
  'application/vnd.ms-excel',
  'text/plain',
];
// Value for the <input accept> attribute on the annex picker.
export const PQRSD_ACCEPT_ATTR =
  'image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt';
// User-facing description of what may be attached.
export const PQRSD_ALLOWED_LABEL = 'Imágenes, PDF, Word, Excel o texto';
// True when a file's MIME type is accepted by the intake cloud function.
export const isAllowedPqrsdMime = (mime) =>
  !!mime && PQRSD_ALLOWED_MIME_PREFIXES.some((p) => mime.startsWith(p));

// PQRSD response kinds. Drives SLA reset semantics and citizen-side UI.
export const PQRSD_RESPONSE_TYPE = {
  INTERIM: 'interim',
  FINAL: 'final',
};

// Lifecycle of the supervised approval workflow attached to a PQRSD response.
// Lives at filed.pqrsd.responseFlowStatus and is mirrored from the linked flow
// doc by the pqrsdResponseFlowSync trigger. Distinct from PQRSD_STATUS so the
// citizen-facing status only flips to RESPONDIDO once the outgoing radicado is
// actually issued (existing pqrsdOnFiledDone path).
export const PQRSD_RESPONSE_FLOW_STATUS = {
  IN_PROGRESS: 'in_progress',
  REJECTED: 'rejected',
  APPROVED: 'approved',
};

// Priority assigned by the AI classifier (or a supervisor override). Stored at
// filed.pqrsd.priority. Drives sorting/urgency in the admin queues alongside the
// existing SLA countdown. Null until the AI runs (AI off → no priority).
export const PQRSD_PRIORITY = {
  BAJA: 'baja',
  MEDIA: 'media',
  ALTA: 'alta',
  URGENTE: 'urgente',
};

// UI-facing labels for the priority chip.
export const PQRSD_PRIORITY_LABELS = {
  baja: 'Baja',
  media: 'Media',
  alta: 'Alta',
  urgente: 'Urgente',
};

// MUI chip colors per priority.
export const PQRSD_PRIORITY_COLORS = {
  baja: 'default',
  media: 'info',
  alta: 'warning',
  urgente: 'error',
};

// Outcome of the server-side AI classifier, stored at filed.pqrsd.aiStatus.
//   null            → AI off / not run; module behaves exactly as before.
//   classified      → confident; routed to a group queue.
//   needs_review    → low confidence / no matching group; lands in Triage.
//   failed          → model/attachment error; lands in Triage with a reason.
export const PQRSD_AI_STATUS = {
  CLASSIFIED: 'classified',
  NEEDS_REVIEW: 'needs_review',
  FAILED: 'failed',
};

// Audit event types appended to filed.events[] by the AI classifier. Labelled
// for the timeline in PqrsdLogsDrawer.js / RadicadoTimeline.js.
export const PQRSD_AI_EVENTS = {
  CLASSIFIED: 'PQRSD_AI_CLASSIFIED',
  ROUTED: 'PQRSD_AI_ROUTED',
  NEEDS_REVIEW: 'PQRSD_AI_NEEDS_REVIEW',
};


export const CONTRACT_STATUS = {
  DRAFT: 'DRAFT',
  SUPERVISOR: 'SUPERVISOR',
  APPROVED: 'APPROVED',
  SUSPENDED: 'SUSPENDED',
  FINISHED: 'FINISHED',
  CLOSED: 'CLOSED'
};

// Estados de revisión por componente de un mes de pago (cada actividad
// individual y la seguridad social del mes). La aprobación es exclusiva
// del Supervisor; la devolución exige observación obligatoria.
export const REVIEW_STATUS = {
  PENDING: 'pending',
  REVIEWED: 'reviewed',
  RETURNED: 'returned',
  APPROVED: 'approved',
};

// Clave de presentación derivada (NO se almacena): un componente en estado
// PENDING pero ya diligenciado/enviado se muestra como 'submitted' (amarillo),
// distinto de PENDING vacío (gris). Se deriva en lectura con
// getReviewDisplayStatus (functions.js).
export const REVIEW_DISPLAY_SUBMITTED = 'submitted';

// Clave de presentación derivada (NO se almacena por actividad): el MES está
// "En corrección" cuando el revisor notificó las devoluciones y el contratista
// aún no notifica su corrección. Se deriva de activitiesReview.correction.
export const REVIEW_DISPLAY_IN_CORRECTION = 'in_correction';

// Amarillo de 'diligenciado/enviado, sin revisar'. No es un color del theme:
// MUI no tiene paleta amarilla; `error` (rojo) se reserva a Devuelta/En corrección.
export const REVIEW_SUBMITTED_COLOR = '#f9a825';

// Clave de presentación derivada (NO se almacena) del estado terminal de la
// seguridad social: `Revisada` es su estado final (no existe Aprobada) y se
// muestra en Verde, a diferencia de la Revisada azul (intermedia) de las
// actividades.
export const REVIEW_DISPLAY_SS_REVIEWED = 'ss_reviewed';

// Ciclo de corrección del mes (payments.payment_N.activitiesReview.correction):
// inicia al enviar la notificación agrupada de devoluciones y cierra cuando el
// contratista notifica la corrección. Solo se conserva el ciclo vigente/último.
export const CORRECTION_STATUS = {
  IN_CORRECTION: 'in_correction',
  CORRECTED: 'corrected',
};

// Mapa ÚNICO de presentación de los estados de revisión: todo chip/badge de
// estado se construye desde aquí (prohibido hardcodear colores de estado en
// los componentes). `color` es un color de la paleta MUI (Chip/palette); `sx`
// (opcional) aplica un color propio cuando el estado no está en la paleta.
// Esquema de 5 banderas: Gris (Pendiente) / Amarillo (Sin revisar) /
// Rojo (Devuelta / En corrección) / Azul (Revisada) / Verde (Aprobada).
export const REVIEW_STATUS_UI = {
  [REVIEW_STATUS.PENDING]: { label: 'Pendiente', color: 'default', Icon: HourglassEmptyIcon },
  [REVIEW_DISPLAY_SUBMITTED]: {
    label: 'Sin revisar',
    color: 'default',
    Icon: PendingActionsIcon,
    sx: {
      color: REVIEW_SUBMITTED_COLOR,
      borderColor: REVIEW_SUBMITTED_COLOR,
      '& .MuiChip-icon': { color: REVIEW_SUBMITTED_COLOR },
    },
  },
  [REVIEW_DISPLAY_IN_CORRECTION]: { label: 'En corrección', color: 'error', Icon: ConstructionIcon },
  [REVIEW_STATUS.REVIEWED]: { label: 'Revisada', color: 'info', Icon: FactCheckIcon },
  [REVIEW_DISPLAY_SS_REVIEWED]: { label: 'Revisada', color: 'success', Icon: FactCheckIcon },
  [REVIEW_STATUS.RETURNED]: { label: 'Devuelta', color: 'error', Icon: AssignmentReturnIcon },
  [REVIEW_STATUS.APPROVED]: { label: 'Aprobada', color: 'success', Icon: TaskAltIcon },
};

export const REVIEW_STATUS_LABELS = Object.fromEntries(
  Object.entries(REVIEW_STATUS_UI).map(([status, ui]) => [status, ui.label])
);

// Campos de la seguridad social mensual que pueden señalarse como
// inconsistentes al devolverla. Las claves corresponden a los campos
// del registro en payments.payment_N.
export const SS_RETURN_FIELDS = {
  contribution: 'Valor de aportes',
  payroll: 'Número de planilla',
  payAt: 'Fecha de pago',
  certificates: 'Comprobante cargado',
};

// Estados de una Solicitud de Pago de un contrato con persona juridica
// (coleccion paymentRequestsLegal). Una sola maquina de estados en tres tramos:
//   empresa   → DRAFT
//   supervisor→ SUBMITTED ⇄ IN_CORRECTION → APPROVED            (C7)
//   financiera→ IN_CAUSATION → IN_ACCOUNTING → IN_BUDGET → IN_TREASURY
//               → PAYMENT_NOTIFIED → PAID                        (acta #3)
// APPROVED conserva su significado (aprobada por supervision) y es la cola de
// recepcion de Causacion. Las transiciones validas viven en
// paymentRequestLogic.js (PAYMENT_REQUEST_TRANSITIONS) por duplicado con el
// servidor; aqui solo los valores.
export const PAYMENT_REQUEST_STATUS = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
  IN_CORRECTION: 'IN_CORRECTION',
  APPROVED: 'APPROVED',
  IN_CAUSATION: 'IN_CAUSATION',
  IN_ACCOUNTING: 'IN_ACCOUNTING',
  IN_BUDGET: 'IN_BUDGET',
  IN_TREASURY: 'IN_TREASURY',
  PAYMENT_NOTIFIED: 'PAYMENT_NOTIFIED',
  PAID: 'PAID',
};

// Mapa UNICO de presentacion de los estados de la solicitud de pago, con la
// misma convencion de REVIEW_STATUS_UI (prohibido hardcodear colores de estado
// en los componentes): Gris = Borrador, Amarillo = Enviada sin revisar,
// Rojo = En correccion (requiere trabajo de la empresa), Verde = Aprobada.
// Los mismos color/icono que REVIEW_STATUS_UI usa para 'En correccion' y
// 'Aprobada', para que el estado de la solicitud y el de sus documentos se lean
// con el mismo vocabulario visual.
// El recorrido financiero (aprobada → ... → pago notificado) se pinta como "en
// transito" con el mismo Amarillo de Enviada: esta en manos de alguien que debe
// actuar. Una devolucion a Causacion NO cambia el color (nunca Naranja): se
// senala aparte con el historial. PAID cierra en Verde.
const IN_TRANSIT_SX = {
  color: REVIEW_SUBMITTED_COLOR,
  borderColor: REVIEW_SUBMITTED_COLOR,
  '& .MuiChip-icon': { color: REVIEW_SUBMITTED_COLOR },
};

export const PAYMENT_REQUEST_STATUS_UI = {
  [PAYMENT_REQUEST_STATUS.DRAFT]: { label: 'Borrador', color: 'default', Icon: EditNoteIcon },
  [PAYMENT_REQUEST_STATUS.SUBMITTED]: {
    label: 'Enviada',
    color: 'default',
    Icon: PendingActionsIcon,
    sx: {
      color: REVIEW_SUBMITTED_COLOR,
      borderColor: REVIEW_SUBMITTED_COLOR,
      '& .MuiChip-icon': { color: REVIEW_SUBMITTED_COLOR },
    },
  },
  [PAYMENT_REQUEST_STATUS.IN_CORRECTION]: {
    label: 'En corrección',
    color: 'error',
    Icon: ConstructionIcon,
  },
  [PAYMENT_REQUEST_STATUS.APPROVED]: {
    label: 'Aprobada',
    color: 'success',
    Icon: TaskAltIcon,
  },
  [PAYMENT_REQUEST_STATUS.IN_CAUSATION]: {
    label: 'En Causación',
    color: 'default',
    Icon: InboxIcon,
    sx: IN_TRANSIT_SX,
  },
  [PAYMENT_REQUEST_STATUS.IN_ACCOUNTING]: {
    label: 'En Contabilidad',
    color: 'default',
    Icon: CalculateIcon,
    sx: IN_TRANSIT_SX,
  },
  [PAYMENT_REQUEST_STATUS.IN_BUDGET]: {
    label: 'En Presupuesto',
    color: 'default',
    Icon: SavingsIcon,
    sx: IN_TRANSIT_SX,
  },
  [PAYMENT_REQUEST_STATUS.IN_TREASURY]: {
    label: 'En Tesorería',
    color: 'default',
    Icon: AccountBalanceIcon,
    sx: IN_TRANSIT_SX,
  },
  [PAYMENT_REQUEST_STATUS.PAYMENT_NOTIFIED]: {
    label: 'Pago notificado',
    color: 'default',
    Icon: PaymentsIcon,
    sx: IN_TRANSIT_SX,
  },
  [PAYMENT_REQUEST_STATUS.PAID]: {
    label: 'Pagada',
    color: 'success',
    Icon: PriceCheckIcon,
  },
};

export const PAYMENT_REQUEST_STATUS_LABELS = Object.fromEntries(
  Object.entries(PAYMENT_REQUEST_STATUS_UI).map(([status, ui]) => [status, ui.label])
);

// Limites de carga de la Solicitud de Pago juridica. MUST stay in sync with the
// server enforcement in gfunctions/handlePaymentRequestLegal/paymentRequestLogic.js
// (MAX_FILE_BYTES, MAX_REPORT_BYTES, MAX_ANNEXES); paymentRequestLogic.test.js
// verifica que front y back no se desincronicen.
// El catalogo administrable (config/contracts.paymentRequestDocs) puede BAJAR el
// limite de una entrada con maxBytes, nunca subirlo por encima de estos topes.
// El informe lleva tope propio y mucho mayor: los informes de obra pueden ser
// libros con planos (decision del daily 2026-08-20).
export const PAYMENT_REQUEST_MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MB
export const PAYMENT_REQUEST_MAX_REPORT_BYTES = 100 * 1024 * 1024; // 100 MB
export const PAYMENT_REQUEST_MAX_ANNEXES = 20;

// Naturaleza del contrato.
// Para contratos con prefijo DEX_, la naturaleza corresponde a uno de los convenios de la
// Dirección de Extensión (NATURES_DEX). Para el resto, las naturalezas estándar (NATURES_DEFAULT).
//
// ⚠️ NATURES_DEFAULT, NATURES_DEX, ALL_NATURES y dependencies son la SEMILLA y el
// FALLBACK del catálogo administrable config/contracts (ContractConfigService.js y la
// copia espejo en gfunctions/handleContract). No eliminar ni renombrar valores aquí:
// la administración de los catálogos se hace desde /contracts/config.
export const NATURES_DEFAULT = [
  "Académico P401",
  "Administrativo P402",
  "Territorios P404",
  "Investigativo P405",
];

export const NATURES_DEX = [
  "Gobernación de Antioquia",
  "Secretaría de Hacienda",
  "Instituto de Cultura y Patrimonio de Antioquia",
  "Lotería de Medellín",
  "Secretaría de las Mujeres - Alcaldía de Medellín",
  "Departamento Administrativo de Planeación - Alcaldía de Medellín",
  "Secretaría de Desarrollo Económico",
  "Secretaría de Talento Humano y Servicios Administrativos",
];

// Universo completo de naturalezas, usado para configurar el acceso por naturaleza
// (allowedNatures) de los financieros (rol FINANCIER) y para los filtros del listado.
export const ALL_NATURES = [...NATURES_DEFAULT, ...NATURES_DEX];

// Prefijo de código que identifica los contratos interadministrativos de la Dirección de
// Extensión. Determina qué listado de naturalezas (convenios) aplica.
export const DEX_PREFIX = "DEX_";

// Devuelve el listado de naturalezas válido según el código del contrato.
export const getNaturesForCode = (contractCode) =>
  contractCode?.startsWith(DEX_PREFIX) ? NATURES_DEX : NATURES_DEFAULT;

// Tipos de contrato: el prefijo del código (DEX_001_2026 → DEX), igual para
// naturales y jurídicos. `users.contractTypes` acota a cuáles tiene acceso un
// usuario (vacío = todos; ADMIN exento). El alcance lo impone el backend, que
// guarda copias de este catálogo: handleContract, handleContractLegal,
// handlePaymentRequestLegal y handleUser. Un prefijo nuevo se agrega en todas.
export const CONTRACT_TYPES = ["CPSDF", "RCPS", "DEX"];

export const CONTRACT_CODE_REGEX = new RegExp(`^(${CONTRACT_TYPES.join("|")})_\\d{3}_\\d{4}$`);

export const getContractType = (contractCode) => String(contractCode || "").split("_")[0];

// Tipos que el usuario puede crear. Solo orienta al formulario: la garantía
// es el backend.
export const getAllowedContractTypes = (user) => {
  const scoped = Array.isArray(user?.contractTypes) ? user.contractTypes : [];
  if (!scoped.length || user?.roles?.includes("ADMIN")) return CONTRACT_TYPES;
  return CONTRACT_TYPES.filter((t) => scoped.includes(t));
};

// Valida el formato del código y que su tipo esté entre los permitidos del
// usuario. Devuelve el mensaje de error o null.
export const validateContractCodeForUser = (contractCode, user) => {
  const allowed = getAllowedContractTypes(user);
  if (!CONTRACT_CODE_REGEX.test(contractCode || "")) {
    return `El código de contrato no tiene un formato válido. Ejemplo: ${allowed[0]}_001_2025. Prefijos admitidos: ${allowed.join(", ")}`;
  }
  if (!allowed.includes(getContractType(contractCode))) {
    return `Solo puedes crear contratos con los prefijos: ${allowed.join(", ")}`;
  }
  return null;
};

export const ARL = {
  L1: { value: "0.00522", text: "Riesgo I" },
  L2: { value: "0.01044", text: "Riesgo II" },
  L3: { value: "0.02436", text: "Riesgo III" },
  L4: { value: "0.04350", text: "Riesgo IV" }
};

export const SALARIO_MINIMO = {
  2024: 1300000,
  2025: 1423500,
  2026: 1750905
};

export const UVT = {
  2024: 52374,
  2025: 49799,
  2026: 47065
};

export const dependencies = [
  'Coordinación de Admisiones y Registro',
  'Coordinación de Adquisición y Bienes',
  'Coordinación de Aseguramiento de la Calidad',
  'Coordinación de Autoevaluación',
  'Coordinación de Ayudas Educativas',
  'Coordinación de Biblioteca',
  'Coordinación de Desarrollo Físico',
  'Coordinación de Egresados',
  'Coordinación de Gestión Documental',
  'Coordinación de Gestión Humana',
  'Coordinación Jurídica',
  'Coordinación de Laboratorios',
  'Coordinación de Presupuesto',
  'Coordinación de Servicios Generales',
  'Coordinación de TIC',
  'Consejo Académico',
  'Consejo Directivo',
  'Departamento de Ciencias Básicas y Áreas Comunes',
  'Dirección Administrativa y Financiera',
  'Dirección de Bienestar',
  'Dirección de Control Interno',
  'Dirección de Extensión',
  'Dirección de Internacionalización',
  'Dirección de Investigación',
  'Dirección de Planeación',
  'Dirección de Regionalización',
  'Facultad de Ciencias Administrativas y Económicas',
  'Facultad de Derecho y Ciencias Forenses',
  'Facultad de Educación y Ciencias Sociales',
  'Facultad de Ingeniería',
  'Oficina de Comunicaciones',
  'Punto TdeA - Mercadeo',
  'Rectoría',
  'Secretaría General',
  'Vicerrectoría Académica',
];

export const getSupervisionFlow = ({
  fecha,
  contract_code,
  contract_contractor,
  titulo,
  nombre_destinatario,
  apellido_destinatario,
  cargo_destinatario,
  dependencia_destinatario,
  entidad_destinatario,
  recipient,
  owner,
}) => ({
  type: "internal",
  document: {
    file: {
      url: "https://firebasestorage.googleapis.com/v0/b/royalplusdev.appspot.com/o/documents%2Ftemplates%2FdXdfD2Wy9ad2vLXY?alt=media&token=0d03006e-6406-40d0-83a4-296eb752519e",
      name: "",
      path: "",
    },
    tags: {
      fecha,
      asunto: "Notificación de Supervisión de contrato",
      contract_code,
      contract_contractor,
      contract_url: window.location.href,
      titulo,
      nombre_destinatario,
      apellido_destinatario,
      cargo_destinatario,
      dependencia_destinatario,
      entidad_destinatario,
      recipient
    },
  },
  actions: [
    {
      owners: [owner],
      type: "",
      tasks: [],
      status: "",
      taskId: "",
      signedAt: {
        seconds: (new Date()).getTime(),
        nanoseconds: 0,
      },
      actionName: "",
      actionNamePP: "",
    },
  ],
  filed: null,
  flowDTO: null,
  attach: "",
  flowId: "",
  status: "",
  subject: "",
  annexes: [],
  userId: "",
  operation: "",
  description: "",
  processName: "",
});
